import { useState } from "react";
import axios from "axios";
import { Phone, Mail, MessageCircle, MapPin, Clock, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { PageHero, GoldButton } from "@/components/Ui";
import CosenzaCard from "@/components/CosenzaCard";
import { SEDI, CONFIG, whatsappLink } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const empty = { nome: "", email: "", telefono: "", oggetto: "", messaggio: "" };

const Contatti = () => {
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nome || !form.email || !form.oggetto || !form.messaggio) { toast.error("Compila tutti i campi obbligatori."); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setDone(true); setForm(empty);
      toast.success("Messaggio inviato! Ti risponderemo al più presto.");
    } catch { toast.error("Errore nell'invio. Riprova."); }
    finally { setSubmitting(false); }
  };

  return (
    <div data-testid="contatti-page">
      <PageHero eyebrow="Contatti" title="Siamo a tua disposizione" subtitle="Scrivici, chiamaci o passa in una delle nostre sedi. Il nostro team è pronto ad assisterti." />

      <CosenzaCard />

      <section className="bg-white py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-12">
          <div>
            <h2 className="font-serif text-3xl font-semibold text-navy-900">Invia un messaggio</h2>
            {done ? (
              <div className="mt-8 bg-[#F8F9FA] border border-gray-100 p-8 text-center" data-testid="contact-success">
                <CheckCircle2 className="w-14 h-14 text-gold-500 mx-auto" />
                <p className="mt-4 font-serif text-2xl text-navy-900">Grazie per averci scritto!</p>
                <p className="mt-2 text-slate-600">Ti risponderemo il prima possibile.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="mt-8 space-y-5" data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div><Label htmlFor="c-nome">Nome *</Label><Input id="c-nome" data-testid="contact-nome" value={form.nome} onChange={(e) => set("nome", e.target.value)} className="mt-2 rounded-none border-gray-200" /></div>
                  <div><Label htmlFor="c-tel">Telefono</Label><Input id="c-tel" data-testid="contact-telefono" value={form.telefono} onChange={(e) => set("telefono", e.target.value)} className="mt-2 rounded-none border-gray-200" /></div>
                </div>
                <div><Label htmlFor="c-email">Email *</Label><Input id="c-email" type="email" data-testid="contact-email" value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-2 rounded-none border-gray-200" /></div>
                <div><Label htmlFor="c-ogg">Oggetto *</Label><Input id="c-ogg" data-testid="contact-oggetto" value={form.oggetto} onChange={(e) => set("oggetto", e.target.value)} className="mt-2 rounded-none border-gray-200" /></div>
                <div><Label htmlFor="c-msg">Messaggio *</Label><Textarea id="c-msg" rows={5} data-testid="contact-messaggio" value={form.messaggio} onChange={(e) => set("messaggio", e.target.value)} className="mt-2 rounded-none border-gray-200" /></div>
                <GoldButton type="submit" disabled={submitting} className="w-full" data-testid="contact-submit-btn"><Send className="w-4 h-4" /> {submitting ? "Invio…" : "Invia messaggio"}</GoldButton>
              </form>
            )}
          </div>

          <div className="space-y-6">
            <div className="bg-navy-900 text-white p-8 space-y-5">
              <a href={CONFIG.phoneHref} className="flex items-center gap-4 hover:text-gold-400 transition-colors" data-testid="contact-phone"><div className="w-11 h-11 bg-gold-500 flex items-center justify-center shrink-0"><Phone className="w-5 h-5 text-navy-900" /></div><div><div className="text-xs uppercase tracking-[0.14em] text-white/60">Telefono</div><div className="font-semibold">{CONFIG.phone}</div></div></a>
              <a href={`mailto:${CONFIG.email}`} className="flex items-center gap-4 hover:text-gold-400 transition-colors" data-testid="contact-email-link"><div className="w-11 h-11 bg-gold-500 flex items-center justify-center shrink-0"><Mail className="w-5 h-5 text-navy-900" /></div><div><div className="text-xs uppercase tracking-[0.14em] text-white/60">Email</div><div className="font-semibold">{CONFIG.email}</div></div></a>
              <a href={whatsappLink()} target="_blank" rel="noreferrer" className="flex items-center gap-4 hover:text-gold-400 transition-colors" data-testid="contact-whatsapp"><div className="w-11 h-11 bg-gold-500 flex items-center justify-center shrink-0"><MessageCircle className="w-5 h-5 text-navy-900" /></div><div><div className="text-xs uppercase tracking-[0.14em] text-white/60">WhatsApp</div><div className="font-semibold">Scrivici ora</div></div></a>
              <div className="flex items-center gap-4"><div className="w-11 h-11 bg-gold-500 flex items-center justify-center shrink-0"><Clock className="w-5 h-5 text-navy-900" /></div><div><div className="text-xs uppercase tracking-[0.14em] text-white/60">Orari</div><div className="font-semibold">Lun–Ven 9:00–13:00 / 15:00–19:00</div></div></div>
            </div>

            <div className="space-y-4">
              {SEDI.map((s) => (
                <a key={s.city} href={s.map} target="_blank" rel="noreferrer" data-testid={`sede-${s.city}`} className="block bg-white border border-gray-100 shadow-crisp p-6 hover:border-gold-500 transition-colors group">
                  <div className="flex items-start gap-4">
                    <MapPin className="w-5 h-5 text-gold-500 shrink-0 mt-1" />
                    <div><div className="font-serif text-xl font-semibold text-navy-900 group-hover:text-gold-600 transition-colors">{s.city}</div><p className="text-sm text-slate-600 mt-1">{s.address}</p></div>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-5 lg:px-8 mt-12">
          <iframe title="Mappa sedi Palumbo Academy" data-testid="contact-map" className="w-full h-[420px] border border-gray-100" loading="lazy" src="https://www.google.com/maps?q=Via%20Giuseppe%20Isnardi%2033%20Cosenza&output=embed" />
        </div>
      </section>
    </div>
  );
};

export default Contatti;
